import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  useTheme,
  alpha,
  ListItemIcon,
  IconButton,
  Tooltip,
  Fade,
  Divider,
  Chip,
  Avatar,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import LanguageIcon from "@mui/icons-material/Language";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import { motion } from "framer-motion";

// SVG bayrak ikonları - Base64 encoded
const FLAGS = {
  tr: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMTUiIHZpZXdCb3g9IjAgMCAyMCAxNSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAiIGhlaWdodD0iMTUiIGZpbGw9IiNFMzBBMTciLz48Y2lyY2xlIGN4PSI3LjUiIGN5PSI3LjUiIHI9IjMuNzUiIGZpbGw9IndoaXRlIi8+PGNpcmNsZSBjeD0iOC4yNSIgY3k9IjcuNSIgcj0iMyIgZmlsbD0iI0UzMEExNyIvPjxwYXRoIGQ9Ik0xMC41IDcuNUwxMi44MjMzIDkuNDM3OEwxMS44NTMyIDYuNTMwMzNMMTQuMTYwMyA0LjU2MjJMMTEuMTI2NiA0LjUzMDMzTDEwLjUgMS41TDkuODczNDQgNC41MzAzM0w2LjgzOTc1IDQuNTYyMkw5LjE0NjgzIDYuNTMwMzNMOC4xNzY3NSA5LjQzNzhMMTAuNSA3LjVaIiBmaWxsPSJ3aGl0ZSIvPjwvc3ZnPg==",
  en: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMTUiIHZpZXdCb3g9IjAgMCAyMCAxNSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAiIGhlaWdodD0iMTUiIGZpbGw9IiMwMTIxNjkiLz48cGF0aCBkPSJNMCAwTDIwIDE1TTIwIDBMMCAxNSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIzIi8+PHBhdGggZD0iTTAgMEwyMCAxNU0yMCAwTDAgMTUiIHN0cm9rZT0iI0M4MTAyRSIgc3Ryb2tlLXdpZHRoPSIyIi8+PHBhdGggZD0iTTEwIDBWMTVNMCA3LjVIMjAiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iNSIvPjxwYXRoIGQ9Ik0xMCAwVjE1TTAgNy41SDIwIiBzdHJva2U9IiNDODEwMkUiIHN0cm9rZS13aWR0aD0iMyIvPjwvc3ZnPg==",
};

const LanguageSwitcher = ({ compact = false, sx = {} }) => {
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [selectedLang, setSelectedLang] = useState(
    i18n.language?.substring(0, 2) || "en"
  );

  // Update selected language whenever i18n language changes
  useEffect(() => {
    const currentLang = i18n.language?.substring(0, 2) || "en";
    setSelectedLang(currentLang);
  }, [i18n.language]);

  const languages = [
    { code: "tr", name: "Türkçe" },
    { code: "en", name: "English" },
  ];

  const handleClick = (event) => {
    // Prevent this click from propagating to parent elements
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem("i18nextLng", langCode);
    setSelectedLang(langCode);
    handleClose();
  };

  const currentLanguage =
    languages.find((lang) => lang.code === selectedLang) || languages[0];

  // Compact version (for mobile or top bars)
  if (compact) {
    return (
      <Box sx={{ ...sx }}>
        <Tooltip title={t("language.select") || "Select Language"} arrow>
          <IconButton
            onClick={handleClick}
            size="small"
            aria-controls={open ? "language-menu-compact" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            sx={{
              color: "#fff",
              padding: "6px",
              backgroundColor: alpha("#fff", 0.08),
              backdropFilter: "blur(4px)",
              transition: "all 0.2s ease",
              borderRadius: "8px",
              border: `1px solid ${alpha("#fff", 0.12)}`,
              "&:hover": {
                backgroundColor: alpha("#fff", 0.15),
                boxShadow: `0 4px 12px ${alpha("#000", 0.15)}`,
              },
              "&:active": {
                backgroundColor: alpha("#fff", 0.2),
              },
            }}
          >
            <Avatar
              src={FLAGS[currentLanguage.code]}
              alt={currentLanguage.name}
              variant="rounded"
              sx={{
                width: 22,
                height: 16,
                borderRadius: "2px",
                filter: "drop-shadow(0px 1px 2px rgba(0,0,0,0.2))",
              }}
            />
          </IconButton>
        </Tooltip>

        <Menu
          id="language-menu-compact"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onClick={(e) => e.stopPropagation()}
          TransitionComponent={Fade}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          MenuListProps={{
            dense: true,
            sx: {
              padding: "8px",
            },
          }}
          PaperProps={{
            elevation: 6,
            sx: {
              mt: 1.5,
              minWidth: 190,
              overflow: "visible",
              borderRadius: "12px",
              backgroundColor: alpha(theme.palette.primary.dark, 0.92),
              backdropFilter: "blur(12px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
              border: `1px solid ${alpha("#fff", 0.06)}`,
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: alpha(theme.palette.primary.dark, 0.92),
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
                borderLeft: `1px solid ${alpha("#fff", 0.06)}`,
                borderTop: `1px solid ${alpha("#fff", 0.06)}`,
              },
            },
          }}
        >
          {languages.map((lang) => (
            <MenuItem
              key={lang.code}
              selected={selectedLang === lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              sx={{
                py: 1.2,
                px: 2,
                my: 0.5,
                mx: 0.8,
                borderRadius: "8px",
                transition: "all 0.2s ease",
                "&.Mui-selected": {
                  backgroundColor: alpha(theme.palette.common.white, 0.12),
                },
                "&:hover": {
                  backgroundColor: alpha(theme.palette.common.white, 0.08),
                  transform: "translateX(2px)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    src={FLAGS[lang.code]}
                    alt={lang.name}
                    variant="rounded"
                    sx={{
                      width: 22,
                      height: 16,
                      borderRadius: "2px",
                      filter: "drop-shadow(0px 1px 1px rgba(0,0,0,0.2))",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: "#fff" }}
                    fontWeight={600}
                  >
                    {lang.name}
                  </Typography>
                </Box>

                {selectedLang === lang.code && (
                  <CheckIcon
                    fontSize="small"
                    sx={{ color: theme.palette.secondary.main }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Menu>
      </Box>
    );
  }

  // Full version
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box sx={{ ...sx }}>
        <Chip
          avatar={
            <Avatar
              src={FLAGS[currentLanguage.code]}
              alt={currentLanguage.name}
              variant="rounded"
              sx={{
                width: 22,
                height: 16,
                borderRadius: "2px",
                "& img": {
                  objectFit: "cover",
                  width: "100%",
                  height: "100%",
                },
              }}
            />
          }
          label={currentLanguage.code.toUpperCase()}
          onClick={handleClick}
          deleteIcon={
            <KeyboardArrowDownIcon
              sx={{
                transition: "transform 0.3s ease",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                fontSize: "1.1rem",
              }}
            />
          }
          onDelete={handleClick}
          aria-controls={open ? "language-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          sx={{
            color: "#fff",
            backgroundColor: alpha(theme.palette.common.white, 0.08),
            borderRadius: "8px",
            fontSize: "0.8rem",
            fontWeight: 600,
            height: 36,
            letterSpacing: 0.5,
            border: `1px solid ${alpha("#fff", 0.12)}`,
            backdropFilter: "blur(5px)",
            transition: "all 0.2s ease",
            boxShadow: open ? `0 0 12px ${alpha("#fff", 0.15)}` : "none",
            "&:hover": {
              backgroundColor: alpha(theme.palette.common.white, 0.15),
              boxShadow: `0 4px 12px ${alpha("#000", 0.15)}`,
            },
            "& .MuiChip-deleteIcon": {
              color: "#fff",
              marginRight: "-4px",
            },
          }}
        />

        <Menu
          id="language-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onClick={(e) => e.stopPropagation()}
          TransitionComponent={Fade}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          MenuListProps={{
            dense: true,
            sx: { padding: "8px" },
          }}
          PaperProps={{
            elevation: 6,
            sx: {
              mt: 1,
              minWidth: 190,
              overflow: "visible",
              borderRadius: "12px",
              backgroundColor: alpha(theme.palette.primary.dark, 0.92),
              backdropFilter: "blur(12px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
              border: `1px solid ${alpha("#fff", 0.06)}`,
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: alpha(theme.palette.primary.dark, 0.92),
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
                borderLeft: `1px solid ${alpha("#fff", 0.06)}`,
                borderTop: `1px solid ${alpha("#fff", 0.06)}`,
              },
            },
          }}
        >
          {languages.map((lang) => (
            <MenuItem
              key={lang.code}
              selected={selectedLang === lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              sx={{
                py: 1.2,
                px: 2,
                my: 0.5,
                mx: 0.5,
                borderRadius: "8px",
                transition: "all 0.2s ease",
                "&.Mui-selected": {
                  backgroundColor: alpha(theme.palette.common.white, 0.12),
                },
                "&:hover": {
                  backgroundColor: alpha(theme.palette.common.white, 0.08),
                  transform: "translateX(2px)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    src={FLAGS[lang.code]}
                    alt={lang.name}
                    variant="rounded"
                    sx={{
                      width: 22,
                      height: 16,
                      borderRadius: "2px",
                      filter: "drop-shadow(0px 1px 1px rgba(0,0,0,0.2))",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: "#fff" }}
                    fontWeight={600}
                  >
                    {lang.name}
                  </Typography>
                </Box>

                {selectedLang === lang.code && (
                  <CheckIcon
                    fontSize="small"
                    sx={{
                      color: theme.palette.secondary.main,
                      filter: "drop-shadow(0px 0px 1px rgba(255,255,255,0.3))",
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </motion.div>
  );
};

export default LanguageSwitcher;